import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyAmountDisplay } from 'src/app/core/services/currency/currency-format';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { NavTab } from 'src/app/core/services/theme/theme-config';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GameId } from 'src/app/model/game/game-id';
import { TabBarComponent } from '../../../ui/tabs/tab-bar/tab-bar.component';
import { GameTitleComponent } from '../../../ui/game-title/game-title.component';
import { GameIconComponent } from '../../../ui/game-icon/game-icon.component';
import { NavButtonComponent } from '../../../ui/buttons/nav-button/nav-button.component';
import { BalancePillComponent } from '../../../ui/balance-pill/balance-pill.component';

import { ExperimentService } from 'src/app/core/services/experiment-service/experiment.service';
import {
  ExperimentId,
  Variant,
} from 'src/app/core/services/experiment-service/models/experiment-id';

export interface TabOption {
  key: string;
  label: string;
  id: string;
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NavButtonComponent,
    GameIconComponent,
    GameTitleComponent,
    BalancePillComponent,
    TabBarComponent,
  ],
})
export class NavbarComponent implements OnInit {
  @Input({ required: true }) gameName!: string;
  @Input({ required: true }) gameId!: GameId;
  @Input() tabs?: TabOption[];
  @Input() balance?: CurrencyAmountDisplay;

  @Output() closeClicked = new EventEmitter<void>();
  @Output() tabSelected = new EventEmitter<string>();

  OperatorService = OperatorService;
  operatorService = inject(OperatorService);
  themeService = inject(ThemeService);
  private analyticsService = inject(AnalyticsService);
  private roomsResultsService = inject(RoomsResultsService);
  private experimentService = inject(ExperimentService);
  private router = inject(Router);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  selectedTabIndex = 0;

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.updateSelectedTab(event.urlAfterRedirects);
      });
  }

  ngOnInit(): void {
    this.updateSelectedTab(this.router.url);
  }

  private updateSelectedTab(url: string): void {
    if (!this.tabs) {
      return;
    }
    const path = url.startsWith('/') ? url.substring(1) : url;
    const index = this.tabs.findIndex((tab) => tab.id === path);
    if (index !== -1) {
      this.selectedTabIndex = index;
      this.changeDetectorRef.markForCheck();
    }
  }

  private readonly isFtueOpponentIncoming = computed(() => {
    this.experimentService.initialised();
    return (
      this.themeService.hasFtueResultsEnabled() ||
      (this.experimentService.getVariant(ExperimentId.FtueOpponentIncoming) === Variant.Test &&
        this.themeService.allowsFtueResults())
    );
  });

  readonly displayedPendingCount = computed(() =>
    this.isFtueOpponentIncoming() ? this.roomsResultsService.pendingCount() || null : null,
  );

  readonly displayedShowDot = computed(
    () => this.isFtueOpponentIncoming() && this.roomsResultsService.hasPending(),
  );

  get resultsTabId(): string | null {
    return this.tabs?.find((tab) => tab.key === NavTab.results)?.id ?? null;
  }

  showBalance = computed(() => {
    const showBalance = this.themeService.header().hasBalance;
    if (this.operatorService.operatorType === 'session-adapter') {
      // Don't show the balance for session operators if the balance is not defined
      return showBalance && this.operatorService.formattedBalance() !== undefined;
    }
    return showBalance;
  });

  onCloseClicked() {
    this.closeClicked.emit();
  }

  onTabSelected(event: string) {
    this.tabSelected.emit(event);
  }

  onBalanceClicked(): void {
    this.analyticsService.log(EventName.NavBarBalancePillClicked);
  }

  onGameIconClicked(): void {
    this.analyticsService.log(EventName.NavBarGameIconClicked);
  }

  onGameTitleClicked(): void {
    this.analyticsService.log(EventName.NavBarGameTitleClicked);
  }
}
